import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { S, Field, Select, Modal, SearchSelect, itemOptions, fmtQty, fmtMoney, fmtDate, today } from './StoresShared';

// Stores module — Phase 2a purchasing screens: Vendors, Purchase Orders,
// Goods Receipt (GRN) and Vendor Bills & Payments.
// API: backend/v1/routes/storesPurchase.js. Rates are per unit, excluding GST.

const STATUS_PILL = {
  DRAFT: ['#f1f5f9', '#475569'], APPROVED: ['#dbeafe', '#1d4ed8'], PARTIAL: ['#fef3c7', '#b45309'],
  RECEIVED: ['#dcfce7', '#15803d'], CLOSED: ['#e2e8f0', '#334155'], CANCELLED: ['#fee2e2', '#b91c1c'],
  POSTED: ['#dcfce7', '#15803d'], OPEN: ['#fef3c7', '#b45309'], PAID: ['#dcfce7', '#15803d'],
  SUBMITTED: ['#fef3c7', '#b45309'], ISSUED: ['#dcfce7', '#15803d'], REJECTED: ['#fee2e2', '#b91c1c'],
};
export const statusPill = (st) => {
  const [bg, fg] = STATUS_PILL[st] || ['#f1f5f9', '#475569'];
  return <span style={S.pill(bg, fg)}>{String(st || '').replace(/_/g, ' ')}</span>;
};
const PAY_MODES = ['BANK', 'UPI', 'CHEQUE', 'CASH', 'CARD', 'OTHER'];
const n = (v) => Number(v) || 0;
const r2 = (v) => Math.round(n(v) * 100) / 100;
const key = () => Math.random().toString(36).slice(2);
// Server dates arrive as ISO timestamps; date inputs want local YYYY-MM-DD.
const ymd = (d) => {
  if (!d) return '';
  const x = new Date(String(d).length === 10 ? `${d}T00:00:00` : d);
  return `${x.getFullYear()}-${String(x.getMonth() + 1).padStart(2, '0')}-${String(x.getDate()).padStart(2, '0')}`;
};
const small = { fontSize: '0.72rem', color: '#94a3b8' };
const can = (boot, ...roles) => boot.me.is_hotel_admin || roles.some((r) => boot.me.roles.includes(r));
const esc = (v) => String(v ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));

const unitsOf = (it) => (it ? [it.base_uom, ...(it.uoms || []).map((u) => u.uom_code)] : []);
const factorOf = (it, uom) => {
  if (!it || !uom || uom === it.base_uom) return 1;
  const u = (it.uoms || []).find((x) => x.uom_code === uom);
  return u ? n(u.factor_to_base) : 1;
};
const buyUnit = (it) => (it?.uoms || []).find((u) => u.is_purchase_uom)?.uom_code || it?.base_uom || '';

// Totals the way the server does: taxable + GST + other charges, rounded to the rupee.
function sumUp(lines, qtyOf, otherCharges) {
  const sub = r2(lines.reduce((s, l) => s + r2(qtyOf(l) * n(l.rate)), 0));
  const tax = r2(lines.reduce((s, l) => s + r2(r2(qtyOf(l) * n(l.rate)) * n(l.tax_rate) / 100), 0));
  const other = r2(Math.max(0, n(otherCharges)));
  const grand = Math.round(sub + tax + other);
  return { sub, tax, other, roundOff: r2(grand - (sub + tax + other)), grand };
}

function Totals({ t, interstate }) {
  const row = (label, v, strong) => (
    <div style={{ display: 'flex', justifyContent: 'space-between', gap: '2rem', fontWeight: strong ? 800 : 400, fontVariantNumeric: 'tabular-nums' }}>
      <span>{label}</span><span>{fmtMoney(v)}</span>
    </div>
  );
  return (
    <div style={{ minWidth: 260, fontSize: '0.86rem', display: 'grid', gap: 2 }}>
      {row('Taxable value', t.sub)}
      {interstate ? row('IGST', t.tax) : <>{row('CGST', r2(t.tax / 2))}{row('SGST', r2(t.tax - r2(t.tax / 2)))}</>}
      {t.other > 0 && row('Other charges', t.other)}
      {Math.abs(t.roundOff) > 0.001 && row('Round off', t.roundOff)}
      {row('Total', t.grand, true)}
    </div>
  );
}

export function useList(api, path) {
  const [rows, setRows] = useState([]);
  const [err, setErr] = useState('');
  const [loading, setLoading] = useState(false);
  const load = useCallback(() => {
    if (!path) { setRows([]); return; }
    setLoading(true); setErr('');
    api(path).then(setRows).catch((e) => setErr(e.message)).finally(() => setLoading(false));
  }, [api, path]);
  useEffect(() => { load(); }, [load]);
  return { rows, err, loading, load };
}

// ── Document view (PO / GRN / bill) ──────────────────────────────────────
export function DocView({ doc, onClose, children }) {
  const isPo = doc.doc_type === 'PO';
  const isGrn = doc.doc_type === 'GRN';
  const t = {
    sub: n(doc.sub_total), tax: n(doc.tax_total), other: n(doc.other_charges), roundOff: n(doc.round_off), grand: n(doc.grand_total),
  };
  const isPr = doc.doc_type === 'PR';
  const title = { PO: 'Purchase order', GRN: 'Goods receipt', VENDOR_BILL: 'Vendor bill', PURCHASE_RETURN: 'Return to vendor', PR: 'Purchase request' }[doc.doc_type] || doc.doc_type;
  return (
    <Modal title={<>{title} {doc.doc_no} {statusPill(doc.status)}</>} onClose={onClose} width={1050}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '0.5rem 1rem', fontSize: '0.86rem', marginBottom: '0.8rem' }}>
        {!isPr && <div><span style={S.lbl}>Vendor</span>{doc.vendor_name || '—'}{doc.vendor_gstin ? <div style={small}>GSTIN {doc.vendor_gstin}</div> : null}</div>}
        <div><span style={S.lbl}>Store</span>{doc.to_store_name || doc.from_store_name || '—'}<div style={small}>{doc.department_name}</div></div>
        <div><span style={S.lbl}>Date</span>{fmtDate(doc.doc_date)}{(isPo || isPr) && doc.expected_date ? <div style={small}>{isPr ? 'needed by' : 'expected'} {fmtDate(doc.expected_date)}</div> : null}</div>
        {isPr && doc.party_name && <div><span style={S.lbl}>Requested by</span>{doc.party_name}</div>}
        {(isGrn || doc.doc_type === 'VENDOR_BILL') && <div><span style={S.lbl}>Vendor invoice</span>{doc.invoice_no || '—'}{doc.invoice_date ? <div style={small}>{fmtDate(doc.invoice_date)}</div> : null}</div>}
        {doc.ref_doc_no && <div><span style={S.lbl}>{doc.ref_doc_type === 'PO' ? 'Purchase order' : 'From'}</span>{doc.ref_doc_no}</div>}
        {doc.doc_type === 'VENDOR_BILL' && <div><span style={S.lbl}>Due</span>{fmtDate(doc.due_date)}<div style={small}>paid {fmtMoney(doc.paid_amount)} · balance {fmtMoney(n(doc.grand_total) - n(doc.paid_amount))}</div></div>}
        <div><span style={S.lbl}>By</span>{doc.created_by || '—'}{doc.approved_by ? <div style={small}>approved by {doc.approved_by}</div> : null}</div>
      </div>
      {doc.cancel_reason && <div style={S.err}>{doc.status === 'CLOSED' ? 'Short-closed' : 'Cancelled'} by {doc.cancelled_by}: {doc.cancel_reason}</div>}
      <div style={S.tableWrap}>
        <table style={S.table}>
          <thead><tr>
            <th style={S.th}>#</th><th style={S.th}>Item</th>
            {isPo && <><th style={{ ...S.th, ...S.num }}>Ordered</th><th style={{ ...S.th, ...S.num }}>Received</th><th style={{ ...S.th, ...S.num }}>Rejected</th><th style={{ ...S.th, ...S.num }}>Pending</th></>}
            {isGrn && <><th style={{ ...S.th, ...S.num }}>Received</th><th style={{ ...S.th, ...S.num }}>Accepted</th><th style={{ ...S.th, ...S.num }}>Rejected</th><th style={{ ...S.th, ...S.num }}>Returned</th><th style={S.th}>Batch / expiry</th></>}
            {!isPo && !isGrn && <th style={{ ...S.th, ...S.num }}>Qty</th>}
            <th style={{ ...S.th, ...S.num }}>{isPr ? 'Est. rate' : 'Rate'}</th>{!isPr && <th style={{ ...S.th, ...S.num }}>GST</th>}<th style={{ ...S.th, ...S.num }}>Amount</th>
          </tr></thead>
          <tbody>
            {doc.lines.map((l) => (
              <tr key={l.id}>
                <td style={S.td}>{l.line_no}</td>
                <td style={S.td}>{l.item_name}<div style={small}>{l.item_code}{l.remarks ? ` · ${l.remarks}` : ''}</div></td>
                {isPo && <>
                  <td style={{ ...S.td, ...S.num }}>{fmtQty(l.qty)} {l.uom_code}</td>
                  <td style={{ ...S.td, ...S.num }}>{fmtQty(l.received_qty)}</td>
                  <td style={{ ...S.td, ...S.num, color: n(l.rejected_qty) ? '#b91c1c' : undefined }}>{n(l.rejected_qty) ? fmtQty(l.rejected_qty) : '—'}</td>
                  <td style={{ ...S.td, ...S.num, fontWeight: 700 }}>{fmtQty(Math.max(0, n(l.qty) - n(l.received_qty)))}</td>
                </>}
                {isGrn && <>
                  <td style={{ ...S.td, ...S.num }}>{fmtQty(l.received_qty)} {l.uom_code}</td>
                  <td style={{ ...S.td, ...S.num, fontWeight: 700 }}>{fmtQty(l.accepted_qty)}</td>
                  <td style={{ ...S.td, ...S.num, color: n(l.rejected_qty) ? '#b91c1c' : undefined }}>{n(l.rejected_qty) ? fmtQty(l.rejected_qty) : '—'}</td>
                  <td style={{ ...S.td, ...S.num, color: n(l.returned_qty) ? '#b91c1c' : undefined }}>{n(l.returned_qty) ? fmtQty(l.returned_qty) : '—'}</td>
                  <td style={S.td}>{l.batch_no || '—'}{l.expiry_date ? ` · ${fmtDate(l.expiry_date)}` : ''}</td>
                </>}
                {!isPo && !isGrn && <td style={{ ...S.td, ...S.num }}>{fmtQty(l.qty)} {l.uom_code}</td>}
                <td style={{ ...S.td, ...S.num }}>{fmtMoney(l.rate)}</td>
                {!isPr && <td style={{ ...S.td, ...S.num }}>{n(l.tax_rate)}%</td>}
                <td style={{ ...S.td, ...S.num }}>{fmtMoney(l.amount)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: '1rem', flexWrap: 'wrap', marginTop: '0.75rem' }}>
        <div style={{ fontSize: '0.84rem', flex: 1, minWidth: 260 }}>
          {doc.remarks && <p style={{ margin: '0 0 0.4rem' }}><strong>Remarks:</strong> {doc.remarks}</p>}
          {doc.terms && <p style={{ margin: '0 0 0.4rem', whiteSpace: 'pre-wrap' }}><strong>Terms:</strong> {doc.terms}</p>}
          {doc.linked_docs?.length > 0 && (
            <div style={{ marginBottom: '0.4rem' }}>
              <strong>Linked:</strong>{' '}
              {doc.linked_docs.map((x) => <span key={x.id} style={{ marginRight: 10 }}>{x.doc_no} {statusPill(x.status)} {fmtMoney(x.grand_total)}</span>)}
            </div>
          )}
          {doc.approval?.levels?.length > 0 && (
            <div style={{ marginBottom: '0.4rem' }}>
              <strong>Approval:</strong>
              {doc.approval.levels.map((lv) => (
                <div key={lv.level}>
                  Level {lv.level} · {lv.roles.map((r) => r.replace(/_/g, ' ').toLowerCase()).join(' or ')} —{' '}
                  {lv.approved_by ? <span style={{ color: '#15803d' }}>approved by {lv.approved_by}</span>
                    : doc.approval.next?.level === lv.level ? <span style={{ color: '#b45309' }}>waiting</span> : <span style={{ color: '#94a3b8' }}>—</span>}
                </div>
              ))}
            </div>
          )}
          {doc.payments?.length > 0 && (
            <div>
              <strong>Payments:</strong>
              {doc.payments.map((p) => (
                <div key={p.id} style={{ textDecoration: p.status === 'CANCELLED' ? 'line-through' : undefined }}>
                  {p.payment_no} · {fmtDate(p.payment_date)} · {p.mode}{p.reference ? ` ${p.reference}` : ''} · {fmtMoney(p.amount)}
                </div>
              ))}
            </div>
          )}
        </div>
        {isPr ? <strong style={{ fontVariantNumeric: 'tabular-nums' }}>Estimated total {fmtMoney(doc.grand_total)}</strong> : <Totals t={t} interstate={doc.is_interstate} />}
      </div>
      {children && <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', justifyContent: 'flex-end', marginTop: '0.9rem', borderTop: '1px solid #e2e8f0', paddingTop: '0.75rem' }}>{children}</div>}
    </Modal>
  );
}

function printPo(doc, property) {
  const w = window.open('', '_blank');
  if (!w) return;
  const rows = doc.lines.map((l) => `<tr><td>${l.line_no}</td><td>${esc(l.item_name)}<br><small>${esc(l.item_code)}</small></td>
    <td class="r">${esc(fmtQty(l.qty))} ${esc(l.uom_code)}</td><td class="r">${esc(fmtMoney(l.rate))}</td>
    <td class="r">${n(l.tax_rate)}%</td><td class="r">${esc(fmtMoney(l.amount))}</td></tr>`).join('');
  const tax = n(doc.tax_total);
  const taxRows = doc.is_interstate
    ? `<tr><td>IGST</td><td class="r">${esc(fmtMoney(tax))}</td></tr>`
    : `<tr><td>CGST</td><td class="r">${esc(fmtMoney(r2(tax / 2)))}</td></tr><tr><td>SGST</td><td class="r">${esc(fmtMoney(tax - r2(tax / 2)))}</td></tr>`;
  w.document.write(`<!doctype html><html><head><meta charset="utf-8"><title>${esc(doc.doc_no)}</title>
    <style>body{font-family:Arial,sans-serif;font-size:12px;color:#111;margin:24px}h1{font-size:18px;margin:0}
    table{width:100%;border-collapse:collapse;margin-top:12px}th,td{border:1px solid #ccc;padding:5px;text-align:left;vertical-align:top}
    th{background:#f1f5f9}.r{text-align:right}.grid{display:flex;justify-content:space-between;gap:24px;margin-top:12px}
    .tot{width:280px}.tot td{border:none;padding:2px 0}</style></head><body>
    <h1>${esc(property?.name || property?.property_name || '')}</h1>
    <h2 style="font-size:15px;margin:6px 0 0">PURCHASE ORDER ${esc(doc.doc_no)}</h2>
    <div class="grid"><div><b>To:</b> ${esc(doc.vendor_name)}<br>${esc(doc.vendor_address || '')}<br>${doc.vendor_gstin ? `GSTIN ${esc(doc.vendor_gstin)}` : ''}</div>
    <div>Date: ${esc(fmtDate(doc.doc_date))}<br>${doc.expected_date ? `Deliver by: ${esc(fmtDate(doc.expected_date))}<br>` : ''}Deliver to: ${esc(doc.to_store_name)}</div></div>
    <table><thead><tr><th>#</th><th>Item</th><th class="r">Qty</th><th class="r">Rate</th><th class="r">GST</th><th class="r">Amount</th></tr></thead><tbody>${rows}</tbody></table>
    <div class="grid"><div>${doc.terms ? `<b>Terms</b><br>${esc(doc.terms).replace(/\n/g, '<br>')}` : ''}${doc.remarks ? `<p>${esc(doc.remarks)}</p>` : ''}</div>
    <table class="tot"><tr><td>Taxable value</td><td class="r">${esc(fmtMoney(doc.sub_total))}</td></tr>${taxRows}
    ${n(doc.other_charges) ? `<tr><td>Other charges</td><td class="r">${esc(fmtMoney(doc.other_charges))}</td></tr>` : ''}
    <tr><td><b>Total</b></td><td class="r"><b>${esc(fmtMoney(doc.grand_total))}</b></td></tr></table></div>
    <p style="margin-top:48px">Authorised signatory${doc.approved_by ? ` — approved by ${esc(doc.approved_by)}` : ''}</p>
    <script>window.onload=function(){window.print()}</script></body></html>`);
  w.document.close();
}

// ── Vendors ──────────────────────────────────────────────────────────────
const emptyVendor = () => ({
  id: null, vendor_code: '', vendor_name: '', contact_person: '', phone: '', email: '', address: '', gstin: '', pan: '',
  is_interstate: false, payment_terms_days: 30, bank_details: '', remarks: '', is_active: true,
});

export function Vendors({ api, boot }) {
  const [q, setQ] = useState('');
  const [showInactive, setShowInactive] = useState(false);
  const path = `/vendors?${new URLSearchParams({ ...(q.trim() ? { q: q.trim() } : {}), ...(showInactive ? {} : { active: '1' }) })}`;
  const { rows, err: listErr, loading, load } = useList(api, path);
  const [form, setForm] = useState(null);
  const [err, setErr] = useState('');
  const [msg, setMsg] = useState('');
  const [saving, setSaving] = useState(false);
  const canCopy = can(boot, 'PURCHASE_MANAGER', 'STORE_MANAGER');

  const save = async () => {
    setSaving(true); setErr('');
    try {
      const v = await api(form.id ? `/vendors/${form.id}` : '/vendors', { method: form.id ? 'PUT' : 'POST', body: form });
      setMsg(`${form.id ? 'Saved' : 'Added'} ${v.vendor_name} (${v.vendor_code}).`);
      setForm(null); load();
    } catch (e) { setErr(e.message); } finally { setSaving(false); }
  };
  const copyExisting = async () => {
    if (!window.confirm('Copy suppliers from Food Inventory and vendors from Maintenance into Stores? Vendors already here (same name) are skipped.')) return;
    setErr(''); setMsg('');
    try {
      const r = await api('/vendors/copy-existing', { method: 'POST' });
      setMsg(r.found ? `Copied ${r.copied} vendor(s); ${r.skipped} were already here.` : 'No suppliers found in Food Inventory or Maintenance.');
      load();
    } catch (e) { setErr(e.message); }
  };
  const f = (k) => ({ value: form[k] ?? '', onChange: (e) => setForm({ ...form, [k]: e.target.value }) });

  return (
    <>
      {(err || listErr) && <div style={S.err}>{err || listErr}</div>}
      {msg && <div style={S.ok}>{msg}</div>}
      <div style={S.bar}>
        <button type="button" className="btn-primary" onClick={() => { setErr(''); setMsg(''); setForm(emptyVendor()); }}>+ New vendor</button>
        {canCopy && <button type="button" className="btn-muted" onClick={copyExisting}>Copy from Food Inventory &amp; Maintenance</button>}
        <Field label="Search"><input style={{ ...S.inp, width: 220 }} value={q} placeholder="Name, code or GSTIN" onChange={(e) => setQ(e.target.value)} /></Field>
        <label style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: '0.85rem', paddingBottom: 6 }}><input type="checkbox" checked={showInactive} onChange={(e) => setShowInactive(e.target.checked)} /> Show inactive</label>
      </div>
      <div style={S.tableWrap}>
        <table style={S.table}>
          <thead><tr>
            <th style={S.th}>Code</th><th style={S.th}>Vendor</th><th style={S.th}>Contact</th><th style={S.th}>GSTIN</th>
            <th style={{ ...S.th, ...S.num }}>Credit days</th><th style={{ ...S.th, ...S.num }}>Outstanding</th><th style={S.th} />
          </tr></thead>
          <tbody>
            {rows.map((v) => (
              <tr key={v.id} style={v.is_active ? undefined : { opacity: 0.55 }}>
                <td style={{ ...S.td, fontFamily: 'monospace' }}>{v.vendor_code}</td>
                <td style={S.td}><div style={{ fontWeight: 600 }}>{v.vendor_name}</div>{!v.is_active && <span style={S.pill('#f1f5f9', '#64748b')}>Inactive</span>}</td>
                <td style={S.td}>{v.contact_person || '—'}<div style={small}>{[v.phone, v.email].filter(Boolean).join(' · ')}</div></td>
                <td style={S.td}>{v.gstin || '—'}{v.is_interstate ? <div style={small}>Other state (IGST)</div> : null}</td>
                <td style={{ ...S.td, ...S.num }}>{v.payment_terms_days}</td>
                <td style={{ ...S.td, ...S.num, fontWeight: n(v.outstanding) ? 700 : 400 }}>{n(v.outstanding) ? fmtMoney(v.outstanding) : '—'}{v.open_bills ? <div style={small}>{v.open_bills} open bill(s)</div> : null}{n(v.credit) > 0.005 ? <div style={{ ...small, color: '#15803d' }}>credit {fmtMoney(v.credit)}</div> : null}</td>
                <td style={S.td}><button type="button" className="btn-muted btn-sm" onClick={() => { setErr(''); setForm({ ...emptyVendor(), ...v }); }}>Edit</button></td>
              </tr>
            ))}
            {!rows.length && <tr><td colSpan={7} style={S.empty}>{loading ? 'Loading…' : 'No vendors yet. Add one, or copy them from Food Inventory and Maintenance.'}</td></tr>}
          </tbody>
        </table>
      </div>
      {form && (
        <Modal title={form.id ? `Edit vendor — ${form.vendor_name}` : 'New vendor'} onClose={() => setForm(null)} width={760}>
          {err && <div style={S.err}>{err}</div>}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '0.7rem' }}>
            <Field label="Vendor name *"><input style={{ ...S.inp, width: '100%' }} {...f('vendor_name')} /></Field>
            <Field label="Code"><input style={{ ...S.inp, width: '100%' }} {...f('vendor_code')} placeholder="Blank = automatic (V-0001)" /></Field>
            <Field label="Contact person"><input style={{ ...S.inp, width: '100%' }} {...f('contact_person')} /></Field>
            <Field label="Phone"><input style={{ ...S.inp, width: '100%' }} {...f('phone')} /></Field>
            <Field label="Email"><input style={{ ...S.inp, width: '100%' }} {...f('email')} /></Field>
            <Field label="GSTIN"><input style={{ ...S.inp, width: '100%' }} {...f('gstin')} placeholder="15 characters" /></Field>
            <Field label="PAN"><input style={{ ...S.inp, width: '100%' }} {...f('pan')} /></Field>
            <Field label="Credit days"><input type="number" min="0" style={{ ...S.inp, width: '100%' }} {...f('payment_terms_days')} /></Field>
            <Field label="Address" style={{ gridColumn: '1 / -1' }}><textarea rows={2} style={{ ...S.inp, width: '100%' }} {...f('address')} /></Field>
            <Field label="Bank details" style={{ gridColumn: '1 / -1' }}><textarea rows={2} style={{ ...S.inp, width: '100%' }} {...f('bank_details')} placeholder="Account name, number, IFSC" /></Field>
            <Field label="Remarks" style={{ gridColumn: '1 / -1' }}><input style={{ ...S.inp, width: '100%' }} {...f('remarks')} /></Field>
          </div>
          <div style={{ display: 'flex', gap: '1.25rem', flexWrap: 'wrap', margin: '0.8rem 0', fontSize: '0.86rem' }}>
            <label style={{ display: 'flex', gap: 6, alignItems: 'center' }}><input type="checkbox" checked={form.is_interstate} onChange={(e) => setForm({ ...form, is_interstate: e.target.checked })} /> Vendor is in another state (charge IGST instead of CGST + SGST)</label>
            <label style={{ display: 'flex', gap: 6, alignItems: 'center' }}><input type="checkbox" checked={form.is_active} onChange={(e) => setForm({ ...form, is_active: e.target.checked })} /> Active</label>
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem' }}>
            <button type="button" className="btn-muted" onClick={() => setForm(null)} disabled={saving}>Cancel</button>
            <button type="button" className="btn-primary" onClick={save} disabled={saving || !form.vendor_name.trim()}>{saving ? 'Saving…' : 'Save vendor'}</button>
          </div>
        </Modal>
      )}
    </>
  );
}

// Shared data for the PO and GRN screens.
function usePurchaseData(api) {
  const [items, setItems] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [err, setErr] = useState('');
  useEffect(() => {
    api('/items?active=1').then(setItems).catch((e) => setErr(e.message));
    api('/vendors?active=1').then(setVendors).catch((e) => setErr(e.message));
  }, [api]);
  const itemById = useMemo(() => Object.fromEntries(items.map((i) => [i.id, i])), [items]);
  return { items, itemById, vendors, err };
}

// ── Purchase orders ──────────────────────────────────────────────────────
const blankPoLine = () => ({ key: key(), item_id: '', uom_code: '', qty: '', rate: '', tax_rate: '', remarks: '' });

export function PurchaseOrders({ api, boot, property }) {
  const { items, itemById, vendors, err: dataErr } = usePurchaseData(api);
  const [status, setStatus] = useState('');
  const [vendorFilter, setVendorFilter] = useState('');
  const path = `/docs?${new URLSearchParams({ doc_type: 'PO', ...(status ? { status } : {}), ...(vendorFilter ? { vendor_id: vendorFilter } : {}) })}`;
  const { rows, err: listErr, loading, load } = useList(api, path);
  const [form, setForm] = useState(null);
  const [view, setView] = useState(null);
  const [err, setErr] = useState('');
  const [msg, setMsg] = useState('');
  const [busy, setBusy] = useState(false);
  const canEdit = can(boot, 'PURCHASE_STAFF', 'PURCHASE_MANAGER', 'STORE_MANAGER');
  const canApprove = can(boot, 'PURCHASE_MANAGER');

  const stores = boot.stores.filter((s) => s.is_active);
  const store = form ? boot.stores.find((s) => s.id === form.store_id) : null;
  const vendor = form ? vendors.find((v) => String(v.id) === String(form.vendor_id)) : null;
  const storeItems = store ? items.filter((i) => i.department_id === store.department_id) : [];
  const t = form ? sumUp(form.lines.filter((l) => l.item_id), (l) => n(l.qty), form.other_charges) : null;

  const setLine = (k, patch) => setForm((fm) => ({ ...fm, lines: fm.lines.map((l) => (l.key === k ? { ...l, ...patch } : l)) }));
  const pickItem = (l, id) => {
    const it = itemById[id];
    const uom = buyUnit(it);
    setLine(l.key, { item_id: id, uom_code: uom, rate: it && n(it.standard_cost) ? String(r2(n(it.standard_cost) * factorOf(it, uom))) : '', tax_rate: it ? String(n(it.gst_rate)) : '' });
  };

  const newPo = () => { setErr(''); setMsg(''); setForm({ id: null, vendor_id: '', store_id: stores[0]?.id || '', doc_date: today(), expected_date: '', other_charges: '', terms: '', remarks: '', lines: [blankPoLine()] }); };
  const editPo = (d) => {
    setView(null); setErr('');
    setForm({
      id: d.id, vendor_id: String(d.vendor_id), store_id: d.to_store_id, doc_date: ymd(d.doc_date) || today(),
      expected_date: ymd(d.expected_date), other_charges: n(d.other_charges) ? String(d.other_charges) : '', terms: d.terms || '', remarks: d.remarks || '',
      lines: d.lines.map((l) => ({ key: key(), item_id: l.item_id, uom_code: l.uom_code, qty: String(n(l.qty)), rate: String(n(l.rate)), tax_rate: String(n(l.tax_rate)), remarks: l.remarks || '' })),
    });
  };

  const save = async (approve) => {
    setBusy(true); setErr('');
    try {
      const body = { ...form, approve, lines: form.lines.filter((l) => l.item_id && n(l.qty) > 0) };
      const d = await api(form.id ? `/po/${form.id}` : '/po', { method: form.id ? 'PUT' : 'POST', body });
      setMsg(`${d.doc_no} ${d.status === 'APPROVED' ? 'saved and approved' : 'saved — waiting for approval'} — ${fmtMoney(d.grand_total)}.`);
      setForm(null); load();
    } catch (e) { setErr(e.message); } finally { setBusy(false); }
  };

  const open = (id) => Promise.all([api(`/docs/${id}`), api(`/approvals/doc/${id}`).catch(() => null)])
    .then(([d, approval]) => { setErr(''); setView({ ...d, approval }); }).catch((e) => setErr(e.message));
  const act = async (path, body, done) => {
    setBusy(true); setErr('');
    try {
      await api(path, { method: 'POST', body });
      setMsg(done); setView(null); load();
    } catch (e) { setErr(e.message); } finally { setBusy(false); }
  };

  return (
    <>
      {(err || listErr || dataErr) && <div style={S.err}>{err || listErr || dataErr}</div>}
      {msg && <div style={S.ok}>{msg}</div>}
      {!form && (
        <div style={S.bar}>
          {canEdit && <button type="button" className="btn-primary" onClick={newPo} disabled={!stores.length}>+ New purchase order</button>}
          <Field label="Status">
            <Select style={S.inp} value={status} onChange={(e) => setStatus(e.target.value)}>
              <option value="">All</option>
              <option value="DRAFT">Draft</option>
              <option value="APPROVED,PARTIAL">Open (to receive)</option>
              <option value="RECEIVED,CLOSED">Received / closed</option>
              <option value="CANCELLED">Cancelled</option>
            </Select>
          </Field>
          <Field label="Vendor">
            <Select style={{ ...S.inp, minWidth: 200 }} value={vendorFilter} onChange={(e) => setVendorFilter(e.target.value)}>
              <option value="">All vendors</option>
              {vendors.map((v) => <option key={v.id} value={v.id}>{v.vendor_name}</option>)}
            </Select>
          </Field>
        </div>
      )}
      {form && (
        <div style={S.card}>
          <h3 style={{ margin: '0 0 0.75rem', fontSize: '1rem', color: '#1e3a5f' }}>{form.id ? 'Edit draft purchase order' : 'New purchase order'}</h3>
          <div style={S.bar}>
            <Field label="Vendor *">
              <SearchSelect style={{ minWidth: 230 }} value={form.vendor_id} options={vendors.map((v) => ({ value: v.id, label: `${v.vendor_name} (${v.vendor_code})` }))} onChange={(v) => setForm({ ...form, vendor_id: v })} />
            </Field>
            <Field label="Deliver to store *">
              <Select style={{ ...S.inp, minWidth: 200 }} value={form.store_id} onChange={(e) => setForm({ ...form, store_id: e.target.value, lines: [blankPoLine()] })}>
                {stores.map((s) => <option key={s.id} value={s.id}>{s.store_name}</option>)}
              </Select>
            </Field>
            <Field label="PO date"><input type="date" style={S.inp} value={form.doc_date} onChange={(e) => setForm({ ...form, doc_date: e.target.value })} /></Field>
            <Field label="Expected by"><input type="date" style={S.inp} value={form.expected_date} onChange={(e) => setForm({ ...form, expected_date: e.target.value })} /></Field>
          </div>
          {!vendors.length && <p style={{ ...S.sub, marginTop: 0 }}>No vendors yet — add them under Stores → Vendors.</p>}
          <div style={S.tableWrap}>
            <table style={S.table}>
              <thead><tr>
                <th style={S.th}>Item</th><th style={S.th}>Unit</th><th style={{ ...S.th, ...S.num }}>Qty</th><th style={{ ...S.th, ...S.num }}>Rate / unit (excl. GST)</th>
                <th style={{ ...S.th, ...S.num }}>GST %</th><th style={{ ...S.th, ...S.num }}>Amount</th><th style={S.th} />
              </tr></thead>
              <tbody>
                {form.lines.map((l) => {
                  const it = itemById[l.item_id];
                  return (
                    <tr key={l.key}>
                      <td style={S.td}>
                        <SearchSelect style={{ minWidth: 240 }} value={l.item_id} options={itemOptions(storeItems)} onChange={(v) => pickItem(l, v)} />
                        {it && l.uom_code !== it.base_uom && <div style={small}>1 {l.uom_code} = {fmtQty(factorOf(it, l.uom_code))} {it.base_uom}</div>}
                      </td>
                      <td style={S.td}>
                        <Select style={{ ...S.inp, width: 90 }} value={l.uom_code} disabled={!it}
                          onChange={(e) => { const f0 = factorOf(it, l.uom_code); const f1 = factorOf(it, e.target.value); setLine(l.key, { uom_code: e.target.value, rate: l.rate !== '' ? String(r2(n(l.rate) / f0 * f1)) : '' }); }}>
                          {unitsOf(it).map((u) => <option key={u} value={u}>{u}</option>)}
                        </Select>
                      </td>
                      <td style={S.td}><input type="number" min="0" step="any" style={{ ...S.inp, width: 90, textAlign: 'right' }} value={l.qty} onChange={(e) => setLine(l.key, { qty: e.target.value })} /></td>
                      <td style={S.td}><input type="number" min="0" step="any" style={{ ...S.inp, width: 110, textAlign: 'right' }} value={l.rate} onChange={(e) => setLine(l.key, { rate: e.target.value })} /></td>
                      <td style={S.td}><input type="number" min="0" max="40" step="any" style={{ ...S.inp, width: 70, textAlign: 'right' }} value={l.tax_rate} onChange={(e) => setLine(l.key, { tax_rate: e.target.value })} /></td>
                      <td style={{ ...S.td, ...S.num }}>{fmtMoney(r2(n(l.qty) * n(l.rate)))}</td>
                      <td style={S.td}><button type="button" className="btn-muted btn-sm" aria-label="Remove line" onClick={() => setForm((fm) => ({ ...fm, lines: fm.lines.length > 1 ? fm.lines.filter((x) => x.key !== l.key) : [blankPoLine()] }))}>✕</button></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {store && !storeItems.length && <p style={{ ...S.sub, marginTop: '0.5rem' }}>No active items in {store.store_name}'s department yet. Add them under Items.</p>}
          <button type="button" className="btn-muted btn-sm" style={{ marginTop: '0.6rem' }} onClick={() => setForm((fm) => ({ ...fm, lines: [...fm.lines, blankPoLine()] }))}>+ Add line</button>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: '1rem', flexWrap: 'wrap', marginTop: '0.8rem' }}>
            <div style={{ flex: 1, minWidth: 280, display: 'grid', gap: '0.6rem' }}>
              <Field label="Other charges (freight, loading …)"><input type="number" min="0" step="any" style={{ ...S.inp, width: 160 }} value={form.other_charges} onChange={(e) => setForm({ ...form, other_charges: e.target.value })} /></Field>
              <Field label="Terms (printed on the PO)"><textarea rows={2} style={{ ...S.inp, width: '100%' }} value={form.terms} onChange={(e) => setForm({ ...form, terms: e.target.value })} placeholder="e.g. Delivery within 3 days. Payment 30 days from invoice." /></Field>
              <Field label="Remarks"><input style={{ ...S.inp, width: '100%' }} value={form.remarks} onChange={(e) => setForm({ ...form, remarks: e.target.value })} /></Field>
            </div>
            <Totals t={t} interstate={vendor?.is_interstate} />
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem', marginTop: '0.9rem' }}>
            <button type="button" className="btn-muted" onClick={() => setForm(null)} disabled={busy}>Cancel</button>
            <button type="button" className="btn-muted" onClick={() => save(false)} disabled={busy || !form.vendor_id}>{busy ? 'Saving…' : 'Save draft'}</button>
            {canApprove && <button type="button" className="btn-primary" onClick={() => save(true)} disabled={busy || !form.vendor_id}>Save &amp; approve</button>}
          </div>
          <p style={{ ...S.sub, textAlign: 'right' }}>The order is approved as per the approval rules (Settings → Approval rules) before goods can be received against it.</p>
        </div>
      )}
      <div style={S.tableWrap}>
        <table style={S.table}>
          <thead><tr>
            <th style={S.th}>PO no.</th><th style={S.th}>Date</th><th style={S.th}>Vendor</th><th style={S.th}>Store</th>
            <th style={{ ...S.th, ...S.num }}>Lines</th><th style={{ ...S.th, ...S.num }}>Value</th><th style={S.th}>Status</th><th style={S.th} />
          </tr></thead>
          <tbody>
            {rows.map((d) => (
              <tr key={d.id}>
                <td style={{ ...S.td, fontWeight: 600, whiteSpace: 'nowrap' }}>{d.doc_no}</td>
                <td style={S.td}>{fmtDate(d.doc_date)}{d.expected_date ? <div style={small}>by {fmtDate(d.expected_date)}</div> : null}</td>
                <td style={S.td}>{d.vendor_name}</td><td style={S.td}>{d.to_store_name}</td>
                <td style={{ ...S.td, ...S.num }}>{d.line_count}</td><td style={{ ...S.td, ...S.num }}>{fmtMoney(d.grand_total)}</td>
                <td style={S.td}>{statusPill(d.status)}</td>
                <td style={S.td}><button type="button" className="btn-muted btn-sm" onClick={() => open(d.id)}>View</button></td>
              </tr>
            ))}
            {!rows.length && <tr><td colSpan={8} style={S.empty}>{loading ? 'Loading…' : 'No purchase orders.'}</td></tr>}
          </tbody>
        </table>
      </div>
      {view && (
        <DocView doc={view} onClose={() => setView(null)}>
          {err && <div style={{ ...S.err, flexBasis: '100%' }}>{err}</div>}
          <button type="button" className="btn-muted" onClick={() => printPo(view, property)}>Print</button>
          {view.status === 'DRAFT' && canEdit && <button type="button" className="btn-muted" onClick={() => editPo(view)} disabled={busy}>Edit</button>}
          {['DRAFT', 'APPROVED', 'PARTIAL'].includes(view.status) && canApprove && (
            <button type="button" className="btn-muted" disabled={busy} onClick={() => {
              const reason = window.prompt(view.status === 'PARTIAL' ? 'Short-close: the rest will not be received. Reason?' : 'Reason for cancelling this purchase order?');
              if (reason) act(`/po/${view.id}/cancel`, { reason }, `${view.doc_no} ${view.status === 'PARTIAL' ? 'short-closed' : 'cancelled'}.`);
            }}>{view.status === 'PARTIAL' ? 'Short-close' : 'Cancel PO'}</button>
          )}
          {view.status === 'DRAFT' && view.approval?.can_approve && <button type="button" className="btn-primary" disabled={busy} onClick={() => act(`/po/${view.id}/approve`, {}, `${view.doc_no}: level ${view.approval.next.level} approved.`)}>Approve level {view.approval.next.level}</button>}
          {view.status === 'DRAFT' && view.approval?.next && !view.approval.can_approve && <span style={{ ...S.sub, alignSelf: 'center' }}>Waiting for {view.approval.next.roles.map((r) => r.replace(/_/g, ' ').toLowerCase()).join(' or ')}.</span>}
          {['APPROVED', 'PARTIAL'].includes(view.status) && <span style={{ ...S.sub, alignSelf: 'center' }}>Receive it under Stores → Goods Receipt (GRN).</span>}
        </DocView>
      )}
    </>
  );
}

// ── Goods receipt (GRN) ──────────────────────────────────────────────────
const blankGrnLine = () => ({
  key: key(), po_line_id: null, item_id: '', uom_code: '', ordered: null, pending: null,
  received_qty: '', accepted_qty: '', reject_reason: '', rate: '', tax_rate: '', batch_no: '', expiry_date: '',
});
const acceptedOf = (l) => (l.accepted_qty === '' ? n(l.received_qty) : n(l.accepted_qty));

export function GoodsReceipts({ api, boot }) {
  const { items, itemById, vendors, err: dataErr } = usePurchaseData(api);
  const [vendorFilter, setVendorFilter] = useState('');
  const path = `/docs?${new URLSearchParams({ doc_type: 'GRN', ...(vendorFilter ? { vendor_id: vendorFilter } : {}) })}`;
  const { rows, err: listErr, loading, load } = useList(api, path);
  const [form, setForm] = useState(null);
  const [openPos, setOpenPos] = useState([]);
  const [view, setView] = useState(null);
  const [billForm, setBillForm] = useState(null);
  const [err, setErr] = useState('');
  const [msg, setMsg] = useState('');
  const [busy, setBusy] = useState(false);
  const canPost = can(boot, 'STOREKEEPER', 'STORE_MANAGER');
  const canBill = can(boot, 'ACCOUNTS', 'STORE_MANAGER');
  const canCancel = can(boot, 'STORE_MANAGER');
  const canReturn = can(boot, 'STORE_MANAGER', 'PURCHASE_MANAGER');
  const [tab, setTab] = useState('receipts');
  const returns = useList(api, tab === 'returns' ? `/docs?${new URLSearchParams({ doc_type: 'PURCHASE_RETURN', ...(vendorFilter ? { vendor_id: vendorFilter } : {}) })}` : null);
  const [returning, setReturning] = useState(null);

  const stores = boot.stores.filter((s) => s.is_active);
  const store = form ? boot.stores.find((s) => s.id === form.store_id) : null;
  const vendor = form ? vendors.find((v) => String(v.id) === String(form.vendor_id)) : null;
  const storeItems = store ? items.filter((i) => i.department_id === store.department_id) : [];
  const t = form ? sumUp(form.lines.filter((l) => l.item_id), acceptedOf, form.other_charges) : null;

  // Open POs for the chosen vendor + store.
  const vendorId = form?.vendor_id;
  const storeId = form?.store_id;
  useEffect(() => {
    if (!vendorId) { setOpenPos([]); return; }
    const p = new URLSearchParams({ vendor_id: vendorId });
    if (storeId) p.set('store_id', storeId);
    api(`/po/open?${p}`).then(setOpenPos).catch((e) => setErr(e.message));
  }, [api, vendorId, storeId]);

  const setLine = (k, patch) => setForm((fm) => ({ ...fm, lines: fm.lines.map((l) => (l.key === k ? { ...l, ...patch } : l)) }));
  const pickItem = (l, id) => {
    const it = itemById[id];
    const uom = buyUnit(it);
    setLine(l.key, { item_id: id, uom_code: uom, rate: it && n(it.standard_cost) ? String(r2(n(it.standard_cost) * factorOf(it, uom))) : '', tax_rate: it ? String(n(it.gst_rate)) : '' });
  };
  const pickPo = async (poId) => {
    if (!poId) { setForm((fm) => ({ ...fm, po_id: '', lines: [blankGrnLine()] })); return; }
    try {
      const po = await api(`/docs/${poId}`);
      const lines = po.lines
        .map((l) => ({ ...blankGrnLine(), po_line_id: l.id, item_id: l.item_id, uom_code: l.uom_code, ordered: n(l.qty), pending: Math.max(0, n(l.qty) - n(l.received_qty)), rate: String(n(l.rate)), tax_rate: String(n(l.tax_rate)) }))
        .filter((l) => l.pending > 0)
        .map((l) => ({ ...l, received_qty: String(l.pending) }));
      setForm((fm) => ({ ...fm, po_id: poId, store_id: po.to_store_id, other_charges: n(po.other_charges) ? String(po.other_charges) : fm.other_charges, lines: lines.length ? lines : [blankGrnLine()] }));
    } catch (e) { setErr(e.message); }
  };

  const newGrn = () => { setErr(''); setMsg(''); setForm({ vendor_id: '', store_id: stores[0]?.id || '', po_id: '', doc_date: today(), invoice_no: '', invoice_date: today(), create_bill: true, other_charges: '', remarks: '', lines: [blankGrnLine()] }); };

  const post = async () => {
    setBusy(true); setErr('');
    try {
      const lines = form.lines.filter((l) => l.item_id && n(l.received_qty) > 0);
      for (const l of lines) {
        const it = itemById[l.item_id];
        const acc = acceptedOf(l);
        if (acc > n(l.received_qty)) throw new Error(`${it?.item_name}: accepted cannot be more than received`);
        if (acc < n(l.received_qty) && l.reject_reason.trim().length < 3) throw new Error(`${it?.item_name}: give a reason for the rejected quantity`);
        if (acc > 0 && it?.expiry_tracked && !l.expiry_date) throw new Error(`${it.item_name}: enter the expiry date`);
      }
      const out = await api('/grn', { method: 'POST', body: { ...form, po_id: form.po_id || null, lines } });
      setMsg(`Posted ${out.grn.doc_no} — ${fmtMoney(out.grn.grand_total)} into ${store?.store_name}.${out.bill ? ` Vendor bill ${out.bill.doc_no} created, due ${fmtDate(out.bill.due_date)}.` : ''}`);
      setForm(null); load();
    } catch (e) { setErr(e.message); } finally { setBusy(false); }
  };

  const open = (id) => api(`/docs/${id}`).then((d) => { setErr(''); setBillForm(null); setView(d); }).catch((e) => setErr(e.message));
  const cancelGrn = async () => {
    const reason = window.prompt(`Cancel ${view.doc_no}? Its stock is taken back out of ${view.to_store_name} and its unpaid bill is cancelled. Reason:`);
    if (!reason) return;
    setBusy(true); setErr('');
    try {
      await api(`/grn/${view.id}/cancel`, { method: 'POST', body: { reason } });
      setMsg(`${view.doc_no} cancelled.`); setView(null); load();
    } catch (e) { setErr(e.message); } finally { setBusy(false); }
  };
  const createBill = async () => {
    setBusy(true); setErr('');
    try {
      const b = await api(`/grn/${view.id}/bill`, { method: 'POST', body: billForm });
      setMsg(`Vendor bill ${b.doc_no} created for ${view.doc_no}.`); setView(null); load();
    } catch (e) { setErr(e.message); } finally { setBusy(false); }
  };

  return (
    <>
      {(err || listErr || dataErr) && !view && <div style={S.err}>{err || listErr || dataErr}</div>}
      {msg && <div style={S.ok}>{msg}</div>}
      {!form && (
        <div style={{ display: 'flex', gap: '0.25rem', borderBottom: '1px solid #e2e8f0', marginBottom: '0.9rem' }}>
          <button type="button" style={S.tab(tab === 'receipts')} onClick={() => setTab('receipts')}>Receipts</button>
          <button type="button" style={S.tab(tab === 'returns')} onClick={() => setTab('returns')}>Returns to vendor</button>
        </div>
      )}
      {!form && (
        <div style={S.bar}>
          {canPost && tab === 'receipts' && <button type="button" className="btn-primary" onClick={newGrn} disabled={!stores.length}>+ Receive goods</button>}
          <Field label="Vendor">
            <Select style={{ ...S.inp, minWidth: 200 }} value={vendorFilter} onChange={(e) => setVendorFilter(e.target.value)}>
              <option value="">All vendors</option>
              {vendors.map((v) => <option key={v.id} value={v.id}>{v.vendor_name}</option>)}
            </Select>
          </Field>
        </div>
      )}
      {form && (
        <div style={S.card}>
          <h3 style={{ margin: '0 0 0.75rem', fontSize: '1rem', color: '#1e3a5f' }}>Receive goods</h3>
          <div style={S.bar}>
            <Field label="Vendor *">
              <SearchSelect style={{ minWidth: 220 }} value={form.vendor_id} options={vendors.map((v) => ({ value: v.id, label: `${v.vendor_name} (${v.vendor_code})` }))} onChange={(v) => setForm({ ...form, vendor_id: v, po_id: '', lines: [blankGrnLine()] })} />
            </Field>
            <Field label="Into store *">
              <Select style={{ ...S.inp, minWidth: 190 }} value={form.store_id} disabled={Boolean(form.po_id)} onChange={(e) => setForm({ ...form, store_id: e.target.value, po_id: '', lines: [blankGrnLine()] })}>
                {stores.map((s) => <option key={s.id} value={s.id}>{s.store_name}</option>)}
              </Select>
            </Field>
            <Field label="Against purchase order">
              <Select style={{ ...S.inp, minWidth: 220 }} value={form.po_id} disabled={!form.vendor_id} onChange={(e) => pickPo(e.target.value)}>
                <option value="">{openPos.length ? 'No PO — direct purchase' : 'No open PO for this vendor'}</option>
                {openPos.map((p) => <option key={p.id} value={p.id}>{p.doc_no} · {fmtDate(p.doc_date)} · {fmtMoney(p.grand_total)}</option>)}
              </Select>
            </Field>
            <Field label="Received on"><input type="date" style={S.inp} value={form.doc_date} onChange={(e) => setForm({ ...form, doc_date: e.target.value })} /></Field>
          </div>
          <div style={S.bar}>
            <Field label={form.create_bill ? 'Vendor invoice no. *' : 'Vendor invoice / DC no.'}><input style={{ ...S.inp, width: 170 }} value={form.invoice_no} onChange={(e) => setForm({ ...form, invoice_no: e.target.value })} /></Field>
            <Field label="Invoice date"><input type="date" style={S.inp} value={form.invoice_date} onChange={(e) => setForm({ ...form, invoice_date: e.target.value })} /></Field>
            <label style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: '0.85rem', paddingBottom: 6 }}>
              <input type="checkbox" checked={form.create_bill} onChange={(e) => setForm({ ...form, create_bill: e.target.checked })} /> Create vendor bill now
            </label>
            {!form.create_bill && <span style={{ ...S.sub, paddingBottom: 6 }}>Bill it later from the GRN when the invoice arrives.</span>}
          </div>
          <div style={S.tableWrap}>
            <table style={S.table}>
              <thead><tr>
                <th style={S.th}>Item</th><th style={S.th}>Unit</th>
                {form.po_id && <th style={{ ...S.th, ...S.num }}>Pending on PO</th>}
                <th style={{ ...S.th, ...S.num }}>Received</th><th style={{ ...S.th, ...S.num }}>Accepted</th><th style={{ ...S.th, ...S.num }}>Rejected</th>
                <th style={{ ...S.th, ...S.num }}>Rate</th><th style={{ ...S.th, ...S.num }}>GST %</th><th style={S.th}>Batch / expiry</th>
                <th style={{ ...S.th, ...S.num }}>Amount</th><th style={S.th} />
              </tr></thead>
              <tbody>
                {form.lines.map((l) => {
                  const it = itemById[l.item_id];
                  const acc = acceptedOf(l);
                  const rej = Math.max(0, n(l.received_qty) - acc);
                  return (
                    <tr key={l.key} style={rej > 0 ? { background: '#fff7ed' } : undefined}>
                      <td style={S.td}>
                        {l.po_line_id ? <><div style={{ fontWeight: 600 }}>{it?.item_name || '…'}</div><div style={small}>{it?.item_code}</div></> : (
                          <SearchSelect style={{ minWidth: 210 }} value={l.item_id} options={itemOptions(storeItems)} onChange={(v) => pickItem(l, v)} />
                        )}
                        {it && l.uom_code !== it.base_uom && <div style={small}>1 {l.uom_code} = {fmtQty(factorOf(it, l.uom_code))} {it.base_uom}</div>}
                      </td>
                      <td style={S.td}>
                        {l.po_line_id ? l.uom_code : (
                          <Select style={{ ...S.inp, width: 80 }} value={l.uom_code} disabled={!it} onChange={(e) => setLine(l.key, { uom_code: e.target.value })}>
                            {unitsOf(it).map((u) => <option key={u} value={u}>{u}</option>)}
                          </Select>
                        )}
                      </td>
                      {form.po_id && <td style={{ ...S.td, ...S.num }}>{l.pending != null ? fmtQty(l.pending) : '—'}</td>}
                      <td style={S.td}><input type="number" min="0" step="any" style={{ ...S.inp, width: 80, textAlign: 'right' }} value={l.received_qty} onChange={(e) => setLine(l.key, { received_qty: e.target.value })} /></td>
                      <td style={S.td}><input type="number" min="0" step="any" style={{ ...S.inp, width: 80, textAlign: 'right' }} value={l.accepted_qty} placeholder={l.received_qty || '0'} onChange={(e) => setLine(l.key, { accepted_qty: e.target.value })} /></td>
                      <td style={{ ...S.td, ...S.num }}>
                        {rej > 0 ? <><span style={{ color: '#b91c1c', fontWeight: 700 }}>{fmtQty(rej)}</span>
                          <input style={{ ...S.inp, width: 130, display: 'block', marginTop: 4 }} value={l.reject_reason} placeholder="Reason *" onChange={(e) => setLine(l.key, { reject_reason: e.target.value })} /></> : '—'}
                      </td>
                      <td style={S.td}><input type="number" min="0" step="any" style={{ ...S.inp, width: 95, textAlign: 'right' }} value={l.rate} onChange={(e) => setLine(l.key, { rate: e.target.value })} /></td>
                      <td style={S.td}><input type="number" min="0" max="40" step="any" style={{ ...S.inp, width: 62, textAlign: 'right' }} value={l.tax_rate} onChange={(e) => setLine(l.key, { tax_rate: e.target.value })} /></td>
                      <td style={S.td}>
                        <input style={{ ...S.inp, width: 105 }} value={l.batch_no} placeholder={it?.batch_tracked ? 'Batch no.' : 'Batch (optional)'} onChange={(e) => setLine(l.key, { batch_no: e.target.value })} />
                        {it?.expiry_tracked && <input type="date" style={{ ...S.inp, display: 'block', marginTop: 4 }} value={l.expiry_date} onChange={(e) => setLine(l.key, { expiry_date: e.target.value })} title="Expiry date" />}
                      </td>
                      <td style={{ ...S.td, ...S.num }}>{fmtMoney(r2(acc * n(l.rate)))}</td>
                      <td style={S.td}><button type="button" className="btn-muted btn-sm" aria-label="Remove line" onClick={() => setForm((fm) => ({ ...fm, lines: fm.lines.length > 1 ? fm.lines.filter((x) => x.key !== l.key) : [blankGrnLine()] }))}>✕</button></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {!form.po_id && <button type="button" className="btn-muted btn-sm" style={{ marginTop: '0.6rem' }} onClick={() => setForm((fm) => ({ ...fm, lines: [...fm.lines, blankGrnLine()] }))}>+ Add line</button>}
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: '1rem', flexWrap: 'wrap', marginTop: '0.8rem' }}>
            <div style={{ flex: 1, minWidth: 280, display: 'grid', gap: '0.6rem' }}>
              <p style={{ ...S.sub, margin: 0 }}>Only the accepted quantity enters stock and is billed. Leave Accepted blank to accept everything received.</p>
              <Field label="Other charges on the invoice (freight …)"><input type="number" min="0" step="any" style={{ ...S.inp, width: 160 }} value={form.other_charges} onChange={(e) => setForm({ ...form, other_charges: e.target.value })} /></Field>
              <Field label="Remarks"><input style={{ ...S.inp, width: '100%' }} value={form.remarks} onChange={(e) => setForm({ ...form, remarks: e.target.value })} /></Field>
            </div>
            <Totals t={t} interstate={vendor?.is_interstate} />
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem', marginTop: '0.9rem' }}>
            <button type="button" className="btn-muted" onClick={() => setForm(null)} disabled={busy}>Cancel</button>
            <button type="button" className="btn-primary" onClick={post} disabled={busy || !form.vendor_id || !form.store_id}>{busy ? 'Posting…' : 'Post GRN'}</button>
          </div>
        </div>
      )}
      {tab === 'returns' && !form && (
        <div style={S.tableWrap}>
          <table style={S.table}>
            <thead><tr>
              <th style={S.th}>Return no.</th><th style={S.th}>Date</th><th style={S.th}>Vendor</th><th style={S.th}>Store</th><th style={S.th}>GRN</th>
              <th style={{ ...S.th, ...S.num }}>Value (incl. GST)</th><th style={S.th} />
            </tr></thead>
            <tbody>
              {returns.rows.map((d) => (
                <tr key={d.id}>
                  <td style={{ ...S.td, fontWeight: 600, whiteSpace: 'nowrap' }}>{d.doc_no}</td><td style={S.td}>{fmtDate(d.doc_date)}</td>
                  <td style={S.td}>{d.vendor_name}</td><td style={S.td}>{d.from_store_name}</td><td style={S.td}>{d.ref_doc_no}</td>
                  <td style={{ ...S.td, ...S.num }}>{fmtMoney(d.grand_total)}</td>
                  <td style={S.td}><button type="button" className="btn-muted btn-sm" onClick={() => open(d.id)}>View</button></td>
                </tr>
              ))}
              {!returns.rows.length && <tr><td colSpan={7} style={S.empty}>{returns.loading ? 'Loading…' : 'Nothing returned to vendors. Open a GRN and press "Return to vendor".'}</td></tr>}
            </tbody>
          </table>
        </div>
      )}
      {tab === 'receipts' && (
      <div style={S.tableWrap}>
        <table style={S.table}>
          <thead><tr>
            <th style={S.th}>GRN no.</th><th style={S.th}>Date</th><th style={S.th}>Vendor</th><th style={S.th}>Store</th><th style={S.th}>Invoice</th>
            <th style={S.th}>PO</th><th style={{ ...S.th, ...S.num }}>Value</th><th style={S.th}>Bill</th><th style={S.th}>Status</th><th style={S.th} />
          </tr></thead>
          <tbody>
            {rows.map((d) => (
              <tr key={d.id}>
                <td style={{ ...S.td, fontWeight: 600, whiteSpace: 'nowrap' }}>{d.doc_no}</td>
                <td style={S.td}>{fmtDate(d.doc_date)}</td><td style={S.td}>{d.vendor_name}</td><td style={S.td}>{d.to_store_name}</td>
                <td style={S.td}>{d.invoice_no || '—'}</td><td style={S.td}>{d.ref_doc_no || '—'}</td>
                <td style={{ ...S.td, ...S.num }}>{fmtMoney(d.grand_total)}</td>
                <td style={S.td}>{d.bill_no || (d.status === 'POSTED' && n(d.grand_total) > 0 ? <span style={S.pill('#fef3c7', '#b45309')}>Not billed</span> : '—')}</td>
                <td style={S.td}>{statusPill(d.status)}</td>
                <td style={S.td}><button type="button" className="btn-muted btn-sm" onClick={() => open(d.id)}>View</button></td>
              </tr>
            ))}
            {!rows.length && <tr><td colSpan={10} style={S.empty}>{loading ? 'Loading…' : 'No goods received yet.'}</td></tr>}
          </tbody>
        </table>
      </div>
      )}
      {returning && (
        <PurchaseReturnDialog api={api} grn={returning} onClose={() => setReturning(null)}
          onDone={(d) => {
            setReturning(null);
            setMsg(`${d.doc_no} posted — ${fmtMoney(d.grand_total)} debit note for ${d.vendor_name || 'the vendor'}${d.credit_applied ? `, ${fmtMoney(d.credit_applied)} set off against bills` : ''}${d.credit_unapplied > 0.005 ? `, ${fmtMoney(d.credit_unapplied)} kept as credit for the next bill` : ''}.`);
            setTab('returns'); returns.load(); load();
          }} />
      )}
      {view && (
        <DocView doc={view} onClose={() => setView(null)}>
          {err && <div style={{ ...S.err, flexBasis: '100%' }}>{err}</div>}
          {view.doc_type === 'GRN' && view.status === 'POSTED' && canBill && n(view.grand_total) > 0 && !view.linked_docs.some((x) => x.doc_type === 'VENDOR_BILL' && x.status !== 'CANCELLED') && (
            billForm ? (
              <>
                <input style={{ ...S.inp, width: 160 }} placeholder="Vendor invoice no." value={billForm.invoice_no} onChange={(e) => setBillForm({ ...billForm, invoice_no: e.target.value })} />
                <input type="date" style={S.inp} value={billForm.invoice_date} onChange={(e) => setBillForm({ ...billForm, invoice_date: e.target.value })} />
                <button type="button" className="btn-primary" onClick={createBill} disabled={busy || !billForm.invoice_no.trim()}>Create bill</button>
              </>
            ) : <button type="button" className="btn-primary" onClick={() => setBillForm({ invoice_no: view.invoice_no || '', invoice_date: today() })}>Create vendor bill</button>
          )}
          {view.doc_type === 'GRN' && view.status === 'POSTED' && canReturn && view.lines.some((l) => n(l.accepted_qty) - n(l.returned_qty) > 0.000001) && (
            <button type="button" className="btn-muted" onClick={() => { setReturning(view); setView(null); setMsg(''); }}>Return to vendor</button>
          )}
          {view.doc_type === 'GRN' && view.status === 'POSTED' && canCancel && <button type="button" className="btn-muted" onClick={cancelGrn} disabled={busy}>Cancel GRN</button>}
        </DocView>
      )}
    </>
  );
}

// ── Vendor bills, payments, outstanding ──────────────────────────────────
export function BillsPayments({ api, boot }) {
  const [tab, setTab] = useState('bills');
  const [vendors, setVendors] = useState([]);
  const [vendorFilter, setVendorFilter] = useState('');
  const [status, setStatus] = useState('open');
  const [view, setView] = useState(null);
  const [pay, setPay] = useState(null);
  const [err, setErr] = useState('');
  const [msg, setMsg] = useState('');
  const canPay = can(boot, 'ACCOUNTS');

  useEffect(() => { api('/vendors').then(setVendors).catch((e) => setErr(e.message)); }, [api]);
  const vq = vendorFilter ? `vendor_id=${vendorFilter}` : '';
  const bills = useList(api, tab === 'bills' ? `/bills?${[vq, status ? `status=${status}` : ''].filter(Boolean).join('&')}` : null);
  const payments = useList(api, tab === 'payments' ? `/payments?${vq}` : null);
  const outstanding = useList(api, tab === 'outstanding' ? '/vendors/outstanding' : null);
  const reload = () => { bills.load(); payments.load(); outstanding.load(); };

  const open = (id) => api(`/docs/${id}`).then(setView).catch((e) => setErr(e.message));
  const cancelPayment = async (p) => {
    const reason = window.prompt(`Cancel payment ${p.payment_no} (${fmtMoney(p.amount)})? The bills it paid become outstanding again. Reason:`);
    if (!reason) return;
    setErr('');
    try {
      await api(`/payments/${p.id}/cancel`, { method: 'POST', body: { reason } });
      setMsg(`Payment ${p.payment_no} cancelled.`); reload();
    } catch (e) { setErr(e.message); }
  };
  const listErr = bills.err || payments.err || outstanding.err;

  return (
    <>
      {(err || listErr) && <div style={S.err}>{err || listErr}</div>}
      {msg && <div style={S.ok}>{msg}</div>}
      <div style={{ display: 'flex', gap: '0.25rem', borderBottom: '1px solid #e2e8f0', marginBottom: '0.9rem' }}>
        <button type="button" style={S.tab(tab === 'bills')} onClick={() => setTab('bills')}>Bills</button>
        <button type="button" style={S.tab(tab === 'payments')} onClick={() => setTab('payments')}>Payments</button>
        <button type="button" style={S.tab(tab === 'outstanding')} onClick={() => setTab('outstanding')}>Outstanding &amp; ageing</button>
      </div>
      <div style={S.bar}>
        {canPay && <button type="button" className="btn-primary" onClick={() => { setMsg(''); setPay({ vendor_id: vendorFilter, bill_id: null }); }}>+ Pay vendor</button>}
        {tab !== 'outstanding' && (
          <Field label="Vendor">
            <Select style={{ ...S.inp, minWidth: 200 }} value={vendorFilter} onChange={(e) => setVendorFilter(e.target.value)}>
              <option value="">All vendors</option>
              {vendors.map((v) => <option key={v.id} value={v.id}>{v.vendor_name}</option>)}
            </Select>
          </Field>
        )}
        {tab === 'bills' && (
          <Field label="Show">
            <Select style={S.inp} value={status} onChange={(e) => setStatus(e.target.value)}>
              <option value="open">Unpaid / part paid</option>
              <option value="PAID">Paid</option>
              <option value="CANCELLED">Cancelled</option>
              <option value="">All</option>
            </Select>
          </Field>
        )}
      </div>

      {tab === 'bills' && (
        <div style={S.tableWrap}>
          <table style={S.table}>
            <thead><tr>
              <th style={S.th}>Bill no.</th><th style={S.th}>Vendor</th><th style={S.th}>Invoice</th><th style={S.th}>GRN</th><th style={S.th}>Due</th>
              <th style={{ ...S.th, ...S.num }}>Total</th><th style={{ ...S.th, ...S.num }}>Paid / adjusted</th><th style={{ ...S.th, ...S.num }}>Balance</th><th style={S.th}>Status</th><th style={S.th} />
            </tr></thead>
            <tbody>
              {bills.rows.map((b) => (
                <tr key={b.id}>
                  <td style={{ ...S.td, fontWeight: 600, whiteSpace: 'nowrap' }}>{b.doc_no}</td>
                  <td style={S.td}>{b.vendor_name}</td>
                  <td style={S.td}>{b.invoice_no}<div style={small}>{fmtDate(b.invoice_date)}</div></td>
                  <td style={S.td}>{b.grn_no || '—'}</td>
                  <td style={S.td}>{fmtDate(b.due_date)}{n(b.overdue_days) > 0 && <div><span style={S.pill('#fee2e2', '#b91c1c')}>{b.overdue_days} days overdue</span></div>}</td>
                  <td style={{ ...S.td, ...S.num }}>{fmtMoney(b.grand_total)}</td>
                  <td style={{ ...S.td, ...S.num }}>{fmtMoney(b.paid_amount)}</td>
                  <td style={{ ...S.td, ...S.num, fontWeight: 700 }}>{fmtMoney(b.balance)}</td>
                  <td style={S.td}>{statusPill(b.status)}</td>
                  <td style={{ ...S.td, whiteSpace: 'nowrap' }}>
                    <button type="button" className="btn-muted btn-sm" onClick={() => open(b.id)}>View</button>{' '}
                    {canPay && ['OPEN', 'PARTIAL'].includes(b.status) && <button type="button" className="btn-primary btn-sm" onClick={() => { setMsg(''); setPay({ vendor_id: String(b.vendor_id), bill_id: b.id }); }}>Pay</button>}
                  </td>
                </tr>
              ))}
              {!bills.rows.length && <tr><td colSpan={10} style={S.empty}>{bills.loading ? 'Loading…' : 'No bills.'}</td></tr>}
            </tbody>
          </table>
        </div>
      )}

      {tab === 'payments' && (
        <div style={S.tableWrap}>
          <table style={S.table}>
            <thead><tr>
              <th style={S.th}>Payment no.</th><th style={S.th}>Date</th><th style={S.th}>Vendor</th><th style={S.th}>Mode / ref.</th>
              <th style={S.th}>Bills paid</th><th style={{ ...S.th, ...S.num }}>Amount</th><th style={S.th}>Status</th><th style={S.th} />
            </tr></thead>
            <tbody>
              {payments.rows.map((p) => (
                <tr key={p.id} style={p.status === 'CANCELLED' ? { opacity: 0.6 } : undefined}>
                  <td style={{ ...S.td, fontWeight: 600, whiteSpace: 'nowrap' }}>{p.payment_no}</td>
                  <td style={S.td}>{fmtDate(p.payment_date)}</td><td style={S.td}>{p.vendor_name}</td>
                  <td style={S.td}>{p.mode}{p.reference ? <div style={small}>{p.reference}</div> : null}</td>
                  <td style={{ ...S.td, fontSize: '0.78rem' }}>{p.allocations.map((a) => <div key={a.bill_id}>{a.doc_no} ({a.invoice_no}) · {fmtMoney(a.amount)}</div>)}</td>
                  <td style={{ ...S.td, ...S.num, fontWeight: 700 }}>{fmtMoney(p.amount)}</td>
                  <td style={S.td}>{statusPill(p.status)}{p.cancel_reason ? <div style={small}>{p.cancel_reason}</div> : null}</td>
                  <td style={S.td}>{canPay && p.status !== 'CANCELLED' && p.mode !== 'DEBIT_NOTE' && <button type="button" className="btn-muted btn-sm" onClick={() => cancelPayment(p)}>Cancel</button>}</td>
                </tr>
              ))}
              {!payments.rows.length && <tr><td colSpan={8} style={S.empty}>{payments.loading ? 'Loading…' : 'No payments yet.'}</td></tr>}
            </tbody>
          </table>
        </div>
      )}

      {tab === 'outstanding' && (
        <div style={S.tableWrap}>
          <table style={S.table}>
            <thead><tr>
              <th style={S.th}>Vendor</th><th style={{ ...S.th, ...S.num }}>Open bills</th><th style={{ ...S.th, ...S.num }}>Not due</th>
              <th style={{ ...S.th, ...S.num }}>1–30 days</th><th style={{ ...S.th, ...S.num }}>31–60</th><th style={{ ...S.th, ...S.num }}>61–90</th>
              <th style={{ ...S.th, ...S.num }}>90+</th><th style={{ ...S.th, ...S.num }}>Outstanding</th><th style={S.th} />
            </tr></thead>
            <tbody>
              {outstanding.rows.map((o) => (
                <tr key={o.vendor_id}>
                  <td style={S.td}><div style={{ fontWeight: 600 }}>{o.vendor_name}</div><div style={small}>{o.vendor_code}{o.gstin ? ` · ${o.gstin}` : ''}</div></td>
                  <td style={{ ...S.td, ...S.num }}>{o.open_bills}</td>
                  <td style={{ ...S.td, ...S.num }}>{n(o.not_due) ? fmtMoney(o.not_due) : '—'}</td>
                  <td style={{ ...S.td, ...S.num }}>{n(o.d1_30) ? fmtMoney(o.d1_30) : '—'}</td>
                  <td style={{ ...S.td, ...S.num, color: n(o.d31_60) ? '#b45309' : undefined }}>{n(o.d31_60) ? fmtMoney(o.d31_60) : '—'}</td>
                  <td style={{ ...S.td, ...S.num, color: n(o.d61_90) ? '#b91c1c' : undefined }}>{n(o.d61_90) ? fmtMoney(o.d61_90) : '—'}</td>
                  <td style={{ ...S.td, ...S.num, color: n(o.d90_plus) ? '#b91c1c' : undefined, fontWeight: n(o.d90_plus) ? 700 : 400 }}>{n(o.d90_plus) ? fmtMoney(o.d90_plus) : '—'}</td>
                  <td style={{ ...S.td, ...S.num, fontWeight: 800 }}>{fmtMoney(o.outstanding)}</td>
                  <td style={S.td}>{canPay && <button type="button" className="btn-primary btn-sm" onClick={() => { setMsg(''); setPay({ vendor_id: String(o.vendor_id), bill_id: null }); }}>Pay</button>}</td>
                </tr>
              ))}
              {outstanding.rows.length > 0 && (
                <tr style={{ background: '#f8fafc', fontWeight: 800 }}>
                  <td style={S.td}>Total</td><td style={S.td} />
                  {['not_due', 'd1_30', 'd31_60', 'd61_90', 'd90_plus', 'outstanding'].map((k) => (
                    <td key={k} style={{ ...S.td, ...S.num }}>{fmtMoney(outstanding.rows.reduce((s, o) => s + n(o[k]), 0))}</td>
                  ))}
                  <td style={S.td} />
                </tr>
              )}
              {!outstanding.rows.length && <tr><td colSpan={9} style={S.empty}>{outstanding.loading ? 'Loading…' : 'Nothing outstanding.'}</td></tr>}
            </tbody>
          </table>
          <p style={{ ...S.sub, padding: '0 0.6rem 0.6rem' }}>Ageing is counted from each bill's due date (invoice date + the vendor's credit days).</p>
        </div>
      )}

      {view && <DocView doc={view} onClose={() => setView(null)} />}
      {pay && (
        <PayDialog api={api} vendors={vendors} initial={pay} onClose={() => setPay(null)}
          onDone={(p) => { setPay(null); setMsg(`Payment ${p.payment_no} of ${fmtMoney(p.amount)} recorded.`); reload(); }} />
      )}
    </>
  );
}

function PayDialog({ api, vendors, initial, onClose, onDone }) {
  const [vendorId, setVendorId] = useState(initial.vendor_id || '');
  const [bills, setBills] = useState([]);
  const [alloc, setAlloc] = useState({});
  const [form, setForm] = useState({ payment_date: today(), mode: 'BANK', reference: '', remarks: '' });
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setBills([]); setAlloc({});
    if (!vendorId) return;
    api(`/bills?vendor_id=${vendorId}&status=open`).then((rows) => {
      setBills(rows);
      // Preselect the bill the dialog was opened from, else nothing.
      if (initial.bill_id) {
        const b = rows.find((x) => x.id === initial.bill_id);
        if (b) setAlloc({ [b.id]: String(r2(b.balance)) });
      }
    }).catch((e) => setErr(e.message));
  }, [api, vendorId, initial.bill_id]);

  const total = r2(Object.values(alloc).reduce((s, v) => s + n(v), 0));
  const toggle = (b, on) => setAlloc((a) => {
    const next = { ...a };
    if (on) next[b.id] = String(r2(b.balance)); else delete next[b.id];
    return next;
  });

  const submit = async () => {
    setBusy(true); setErr('');
    try {
      const allocations = Object.entries(alloc).map(([bill_id, amount]) => ({ bill_id, amount: n(amount) })).filter((a) => a.amount > 0);
      const p = await api('/payments', { method: 'POST', body: { ...form, vendor_id: vendorId, amount: total, allocations } });
      onDone(p);
    } catch (e) { setErr(e.message); } finally { setBusy(false); }
  };

  return (
    <Modal title="Pay vendor" onClose={onClose} width={860}>
      {err && <div style={S.err}>{err}</div>}
      <div style={S.bar}>
        <Field label="Vendor *">
          <SearchSelect style={{ minWidth: 230 }} value={vendorId} options={vendors.map((v) => ({ value: v.id, label: `${v.vendor_name}${n(v.outstanding) ? ` — ${fmtMoney(v.outstanding)} due` : ''}` }))} onChange={setVendorId} />
        </Field>
        <Field label="Date"><input type="date" style={S.inp} value={form.payment_date} onChange={(e) => setForm({ ...form, payment_date: e.target.value })} /></Field>
        <Field label="Mode">
          <Select style={S.inp} value={form.mode} onChange={(e) => setForm({ ...form, mode: e.target.value })}>
            {PAY_MODES.map((m) => <option key={m} value={m}>{m}</option>)}
          </Select>
        </Field>
        <Field label="Reference (UTR / cheque no.)"><input style={{ ...S.inp, width: 170 }} value={form.reference} onChange={(e) => setForm({ ...form, reference: e.target.value })} /></Field>
      </div>
      <div style={{ ...S.tableWrap, maxHeight: '45vh', overflowY: 'auto' }}>
        <table style={S.table}>
          <thead><tr>
            <th style={S.th} /><th style={S.th}>Bill</th><th style={S.th}>Invoice</th><th style={S.th}>Due</th>
            <th style={{ ...S.th, ...S.num }}>Balance</th><th style={{ ...S.th, ...S.num }}>Pay now</th>
          </tr></thead>
          <tbody>
            {bills.map((b) => {
              const on = alloc[b.id] != null;
              return (
                <tr key={b.id}>
                  <td style={S.td}><input type="checkbox" checked={on} onChange={(e) => toggle(b, e.target.checked)} aria-label={`Pay ${b.doc_no}`} /></td>
                  <td style={{ ...S.td, fontWeight: 600 }}>{b.doc_no}</td>
                  <td style={S.td}>{b.invoice_no}<div style={small}>{fmtDate(b.invoice_date)}</div></td>
                  <td style={S.td}>{fmtDate(b.due_date)}{n(b.overdue_days) > 0 && <div style={{ ...small, color: '#b91c1c' }}>{b.overdue_days} days overdue</div>}</td>
                  <td style={{ ...S.td, ...S.num }}>{fmtMoney(b.balance)}</td>
                  <td style={S.td}><input type="number" min="0" step="any" disabled={!on} style={{ ...S.inp, width: 120, textAlign: 'right', marginLeft: 'auto', display: 'block' }} value={alloc[b.id] ?? ''} onChange={(e) => setAlloc({ ...alloc, [b.id]: e.target.value })} /></td>
                </tr>
              );
            })}
            {!bills.length && <tr><td colSpan={6} style={S.empty}>{vendorId ? 'No unpaid bills for this vendor.' : 'Choose a vendor.'}</td></tr>}
          </tbody>
        </table>
      </div>
      <Field label="Remarks" style={{ marginTop: '0.75rem' }}><input style={{ ...S.inp, width: '100%' }} value={form.remarks} onChange={(e) => setForm({ ...form, remarks: e.target.value })} /></Field>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap', marginTop: '0.9rem' }}>
        <strong style={{ fontSize: '1.05rem', fontVariantNumeric: 'tabular-nums' }}>Paying {fmtMoney(total)}</strong>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <button type="button" className="btn-muted" onClick={onClose} disabled={busy}>Cancel</button>
          <button type="button" className="btn-primary" onClick={submit} disabled={busy || !vendorId || !(total > 0)}>{busy ? 'Saving…' : 'Record payment'}</button>
        </div>
      </div>
    </Modal>
  );
}

// ── Return to vendor (from a GRN) ────────────────────────────────────────
const RETURN_REASONS = [['DAMAGED', 'Damaged'], ['QUALITY', 'Quality issue'], ['EXPIRED', 'Expired / short expiry'], ['EXCESS', 'Excess supplied'], ['WRONG_ITEM', 'Wrong item'], ['OTHER', 'Other']];

function PurchaseReturnDialog({ api, grn, onClose, onDone }) {
  const [form, setForm] = useState(() => ({
    doc_date: today(), debit_note_ref: '', remarks: '',
    lines: grn.lines.map((l) => ({
      key: key(), grn_line_id: l.id, item_name: l.item_name, item_code: l.item_code, uom_code: l.uom_code, rate: n(l.rate), tax_rate: n(l.tax_rate),
      left: Math.round((n(l.accepted_qty) - n(l.returned_qty)) * 10000) / 10000, qty: '', reason: 'DAMAGED',
    })),
  }));
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  const setLine = (k, patch) => setForm((f) => ({ ...f, lines: f.lines.map((l) => (l.key === k ? { ...l, ...patch } : l)) }));
  const t = sumUp(form.lines.filter((l) => n(l.qty) > 0), (l) => n(l.qty), 0);
  const post = async () => {
    setBusy(true); setErr('');
    try {
      const lines = form.lines.filter((l) => n(l.qty) > 0).map((l) => ({ grn_line_id: l.grn_line_id, qty: l.qty, reason: l.reason, remarks: l.remarks }));
      if (!lines.length) throw new Error('Enter the quantity going back');
      const d = await api('/purchase-returns', { method: 'POST', body: { ...form, grn_id: grn.id, lines } });
      onDone({ ...d, vendor_name: grn.vendor_name });
    } catch (e) { setErr(e.message); } finally { setBusy(false); }
  };
  return (
    <Modal title={`Return to ${grn.vendor_name} — from ${grn.doc_no}`} onClose={onClose} width={920}>
      {err && <div style={S.err}>{err}</div>}
      <div style={S.bar}>
        <Field label="Date"><input type="date" style={S.inp} value={form.doc_date} onChange={(e) => setForm({ ...form, doc_date: e.target.value })} /></Field>
        <Field label="Vendor credit note no. (if any)"><input style={{ ...S.inp, width: 180 }} value={form.debit_note_ref} onChange={(e) => setForm({ ...form, debit_note_ref: e.target.value })} /></Field>
      </div>
      <div style={S.tableWrap}>
        <table style={S.table}>
          <thead><tr>
            <th style={S.th}>Item</th><th style={{ ...S.th, ...S.num }}>Can return</th><th style={{ ...S.th, ...S.num }}>Qty back</th>
            <th style={S.th}>Reason</th><th style={{ ...S.th, ...S.num }}>Rate</th><th style={{ ...S.th, ...S.num }}>Amount</th>
          </tr></thead>
          <tbody>
            {form.lines.map((l) => (
              <tr key={l.key}>
                <td style={S.td}>{l.item_name}<div style={small}>{l.item_code}</div></td>
                <td style={{ ...S.td, ...S.num }}>{fmtQty(l.left)} {l.uom_code}</td>
                <td style={S.td}><input type="number" min="0" max={l.left} step="any" disabled={l.left <= 0} style={{ ...S.inp, width: 90, textAlign: 'right', marginLeft: 'auto', display: 'block' }} value={l.qty} onChange={(e) => setLine(l.key, { qty: e.target.value })} /></td>
                <td style={S.td}><Select style={S.inp} value={l.reason} onChange={(e) => setLine(l.key, { reason: e.target.value })}>{RETURN_REASONS.map(([k, v]) => <option key={k} value={k}>{v}</option>)}</Select></td>
                <td style={{ ...S.td, ...S.num }}>{fmtMoney(l.rate)}</td>
                <td style={{ ...S.td, ...S.num }}>{fmtMoney(r2(n(l.qty) * l.rate))}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: '1rem', flexWrap: 'wrap', marginTop: '0.8rem' }}>
        <div style={{ flex: 1, minWidth: 260 }}>
          <Field label="Remarks"><input style={{ ...S.inp, width: '100%' }} value={form.remarks} onChange={(e) => setForm({ ...form, remarks: e.target.value })} /></Field>
          <p style={S.sub}>Stock goes out of the batch this GRN received. The value with GST is a debit note: it is set off against this vendor's unpaid bills, or kept as credit for the next bill.</p>
        </div>
        <Totals t={t} interstate={grn.is_interstate} />
      </div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem', marginTop: '0.6rem' }}>
        <button type="button" className="btn-muted" onClick={onClose} disabled={busy}>Cancel</button>
        <button type="button" className="btn-primary" onClick={post} disabled={busy || !(t.sub > 0)}>{busy ? 'Posting…' : 'Post return'}</button>
      </div>
    </Modal>
  );
}
